import { useContext } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import {
  StableRouterContext,
  type StableLocationRead,
  type StableRouterValue,
} from '@providers/stableRouterContext';

function useStableRouter(hookName: string): StableRouterValue {
  const context = useContext(StableRouterContext);
  if (context === undefined) {
    throw new Error(`${hookName} must be used within a StableRouterProvider`);
  }
  return context;
}

/**
 * Navigate without subscribing to the router.
 *
 * Drop-in for `useNavigate()` in any component that navigates but does not render anything derived
 * from the URL - list leaves above all, where `useNavigate()`'s hidden `RouteContext` read costs a
 * re-render of the entire list on every navigation. See `stableRouterContext.ts`.
 *
 * Not a replacement for `useNavigate()` in components that DO render from the URL: those want the
 * subscription, and swapping them over would just make them stale.
 */
export function useStableNavigate(): NavigateFunction {
  return useStableRouter('useStableNavigate').navigate;
}

/**
 * Read the current location on demand, without subscribing to the router.
 *
 * The returned function is identity-stable, so it is free to list in a dependency array and never
 * invalidates a `useCallback` or a `useMemo`.
 *
 * WHERE IT IS SAFE TO CALL:
 * - Event handlers, timers, and async continuations: yes.
 * - `useEffect` bodies: yes. `StableRouterProvider` syncs its ref in a LAYOUT effect, and every
 *   layout effect runs before any passive effect, so a passive effect sees the current URL even on
 *   the commit that mounted the component.
 * - During render: NO. A component using this hook does not re-render when the URL moves, so the
 *   value would freeze at mount and every `returnTo` it stamps would point at a page the user left.
 * - `useLayoutEffect` bodies: NO. Layout effects run child-first, so a descendant's runs before the
 *   provider's sync and reads the previous location.
 *
 * The return is deliberately narrower than react-router's `Location` - `{ pathname, search }` only.
 * Nothing mechanical can stop the render-phase call above, because this is an ordinary function and
 * the lint rules that police hooks do not see it, so the type does what it can: it withholds `state`
 * and `key`, the two fields that are meaningful only for one specific history entry and so are the
 * ones a frozen read corrupts most quietly. See `StableLocationRead`.
 */
export function useReadLocation(): () => StableLocationRead {
  return useStableRouter('useReadLocation').readLocation;
}
